/**
 * Trigger System
 * 
 * Manages behavior-based triggers. Replaces the legacy TriggerManager.
 * Triggers are entities that fire scriptable behaviors when entities enter/exit zones.
 */

import { Vector3, VertexBuffer, Scene, MeshBuilder, StandardMaterial, Color3, type Mesh, type AbstractMesh, type BoundingBox } from '@babylonjs/core';
import { logger } from '../core/Logger';
import { BehaviorRegistry } from '../core/BehaviorRegistry';
import { EntityRegistry } from '../core/EntityRegistry';
import { ServiceRegistry } from '../core/ServiceRegistry';
import { EntityType, type EntityInfo } from '../types/EntityTypes';
import type { 
  TriggerState,
  TriggerBehavior,
  TriggerEnterAPI,
  TriggerExitAPI,
  TriggerTickAPI,
  TriggerContextBase,
  SceneServices
} from '../types/TriggerTypes';
import { EventBus } from '../core/EventBus';

/**
 * Options for registering a trigger
 */
export interface RegisterTriggerOptions {
  id: string;
  behaviorId: string;
  position: Vector3;
  /** Reference to mesh for dynamic bounding box computation */
  mesh?: AbstractMesh;
  /** Bounding box for geometry-based detection (preferred) */
  boundingBox?: BoundingBox;
  oneShot?: boolean;
  params?: Record<string, unknown>;
}

/**
 * TriggerSystem - Manages behavior-based trigger zones
 */
export class TriggerSystem {
  private triggers = new Map<string, TriggerState>();
  private entityRegistry: EntityRegistry;
  private eventBus: EventBus;
  private scene: Scene | null = null;
  private debugMeshes = new Map<string, AbstractMesh>();
  private debugVisualizationEnabled = false;
  private playerDebugSphere: AbstractMesh | null = null;
  
  constructor(entityRegistry: EntityRegistry, eventBus: EventBus) {
    this.entityRegistry = entityRegistry;
    this.eventBus = eventBus;
    logger.triggers.log('✅ TriggerSystem initialized');
  }

  /**
   * Set the scene for debug visualization
   */
  setScene(scene: Scene): void {
    this.scene = scene;
  }
  
  // ============================================
  // REGISTRATION
  // ============================================
  
  /**
   * Register a trigger from a mesh with metadata
   * 
   * @throws Error if mesh doesn't have trigger behavior set
   */
  registerFromMesh(mesh: AbstractMesh): void {
    const metadata = mesh.metadata || {};
    const extras = metadata.gltf?.extras || metadata;
    
    logger.triggers.debug(`[registerFromMesh] ${mesh.name} - raw extras:`, extras);
    
    // Get behavior ID from hm_behaviors (new format: 'trigger:gravity_zone')
    // or fall back to legacy hm_behavior_id
    let behaviorId: string | undefined;
    
    const behaviorsStr = extras.hm_behaviors || '';
    if (behaviorsStr) {
      // Find the trigger:* behavior in the comma-separated list
      const behaviors = behaviorsStr.split(',').map((b: string) => b.trim());
      const triggerBehavior = behaviors.find((b: string) => b.startsWith('trigger:'));
      if (triggerBehavior) {
        // Extract the full behavior ID (e.g., 'trigger:gravity_zone')
        behaviorId = triggerBehavior;
      }
    }
    
    // Legacy fallback: hm_behavior_id
    if (!behaviorId) {
      behaviorId = extras.hm_behavior_id || extras.behavior_id;
    }
    
    if (!behaviorId) {
      throw new Error(`TriggerSystem: Mesh "${mesh.name}" has no trigger behavior. Expected hm_behaviors with 'trigger:*' format.`);
    }
    logger.triggers.log(`[registerFromMesh] ${mesh.name} - resolved behaviorId: ${behaviorId}`);
    
    // Get trigger properties
    const oneShot = extras.hm_oneShot ?? extras.oneShot ?? false;
    
    // Extract behavior parameters (hm_behavior_*)
    const params: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(extras)) {
      if (key.startsWith('hm_behavior_') && key !== 'hm_behavior_id') {
        const paramName = key.replace('hm_behavior_', '');
        params[paramName] = value;
      }
    }
    
    // Store mesh reference for dynamic bounding box computation
    // Don't precompute bounding box - it may change after parent transforms settle
    logger.triggers.log(`[registerFromMesh] ${mesh.name} - storing mesh reference for dynamic BB`);
    
    this.register({
      id: mesh.name,
      behaviorId,
      position: mesh.position.clone(),
      mesh,  // Store mesh reference
      oneShot,
      params
    });
    
    // Hide the trigger mesh but keep it for bounding box computation
    mesh.isVisible = false;
    mesh.checkCollisions = false;
  }
  
  /**
   * Register a trigger manually
   * 
   * @throws Error if behaviorId is not registered
   */
  register(options: RegisterTriggerOptions): void {
    const { id, behaviorId, position, mesh, boundingBox, oneShot = false, params = {} } = options;
    
    // Get behavior (throws if not found - fail fast!)
    const { behavior, resolvedId } = BehaviorRegistry.get(behaviorId);
    const triggerBehavior = behavior as TriggerBehavior;
    
    // Create trigger state
    const trigger: TriggerState = {
      id,
      behaviorId: resolvedId,
      position: position.clone(),
      mesh,  // Store mesh reference for dynamic bounding box
      boundingBox,
      oneShot,
      activated: false,
      params,
      entitiesInside: new Set(),
      metadata: {}
    };
    
    this.triggers.set(id, trigger);
    
    // Register as entity
    this.entityRegistry.registerEntityInfo({
      id,
      type: EntityType.TRIGGER_ZONE,
      behaviorId: trigger.behaviorId,
      displayName: `Trigger: ${resolvedId}`,
      isPlayer: false,
      metadata: { ...params, oneShot, hasBoundingBox: !!boundingBox, hasMesh: !!mesh }
    });
    
    const detectionMethod = mesh ? 'mesh AABB (dynamic)' : boundingBox ? 'bounding box' : 'INVALID - no geometry';
    logger.triggers.log(`✅ Registered trigger: ${id}`);
    logger.triggers.log(`   📍 Position: (${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)})`);
    logger.triggers.log(`   🎯 Behavior: ${resolvedId}`);
    logger.triggers.log(`   📏 Detection: ${detectionMethod}`);
    logger.triggers.log(`   🔧 Hooks: onEnter=${!!triggerBehavior.onEnter}, onExit=${!!triggerBehavior.onExit}, onTick=${!!triggerBehavior.onTick}, onBind=${!!triggerBehavior.onBind}`);
    
    // Call onBind hook if defined
    if (triggerBehavior.onBind) {
      try {
        const entityInfo = this.entityRegistry.getEntityInfo(id);
        if (entityInfo) {
          triggerBehavior.onBind({
            triggerId: id,
            behaviorId: resolvedId,
            position: trigger.position,
            params: trigger.params,
            entityInfo
          });
          logger.triggers.log(`   ✅ onBind called for ${id}`);
        }
      } catch (error) {
        logger.triggers.error(`   ❌ onBind error for ${id}:`, error);
      }
    }

    // Create debug mesh if visualization is enabled
    if (this.debugVisualizationEnabled && this.scene) {
      this.createDebugMeshForTrigger(id, trigger);
    }
  }
  
  /**
   * Unregister a trigger
   */
  unregister(id: string): void {
    const trigger = this.triggers.get(id);
    if (!trigger) return;
    
    // Fire exit events for any entities still inside
    for (const entityId of trigger.entitiesInside) {
      this.fireExitEvent(trigger, entityId);
    }
    
    this.triggers.delete(id);
    this.entityRegistry.unregisterEntityInfo(id);
    logger.triggers.debug(`Unregistered trigger: ${id}`);
  }
  
  // ============================================
  // UPDATE LOOP
  // ============================================
  
  private _firstUpdateLogged = false;
  
  /**
   * Check if a position is inside a trigger zone
   */
  private isInsideTrigger(trigger: TriggerState, position: Vector3): boolean {
    
    // Prefer mesh reference - compute world-space AABB from raw vertices
    if (trigger.mesh) {
      const mesh = trigger.mesh as Mesh;
      
      // Force world matrix computation for entire parent chain
      mesh.computeWorldMatrix(true);
      const worldMatrix = mesh.getWorldMatrix();
      
      // Get raw vertex positions
      const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
      if (positions && positions.length >= 3) {
        // Compute world-space AABB from all vertices
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        
        for (let i = 0; i < positions.length; i += 3) {
          // Transform local vertex to world space
          const localVert = new Vector3(positions[i], positions[i + 1], positions[i + 2]);
          const worldVert = Vector3.TransformCoordinates(localVert, worldMatrix);
          
          if (worldVert.x < minX) minX = worldVert.x;
          if (worldVert.y < minY) minY = worldVert.y;
          if (worldVert.z < minZ) minZ = worldVert.z;
          if (worldVert.x > maxX) maxX = worldVert.x;
          if (worldVert.y > maxY) maxY = worldVert.y;
          if (worldVert.z > maxZ) maxZ = worldVert.z;
        }
        
        // Debug: Log AABB bounds once per trigger
        if (!trigger.metadata._aabbLogged) {
          trigger.metadata._aabbLogged = true;
          logger.triggers.log(`[${trigger.id}] AABB bounds: X=[${minX.toFixed(2)}, ${maxX.toFixed(2)}] Y=[${minY.toFixed(2)}, ${maxY.toFixed(2)}] Z=[${minZ.toFixed(2)}, ${maxZ.toFixed(2)}]`);
        }
        
        const result = 
          position.x >= minX && position.x <= maxX &&
          position.y >= minY && position.y <= maxY &&
          position.z >= minZ && position.z <= maxZ;
        
        return result;
      }
    }
    
    // Static bounding box fallback
    if (trigger.boundingBox) {
      const bb = trigger.boundingBox;
      const min = bb.minimum;
      const max = bb.maximum;
      const result = 
        position.x >= min.x && position.x <= max.x &&
        position.y >= min.y && position.y <= max.y &&
        position.z >= min.z && position.z <= max.z;
      return result;
    }
    
    // No valid detection method
    logger.triggers.warn(`⚠️ Trigger ${trigger.id} has no mesh or bounding box for detection`);
    return false;
  }
  
  /**
   * Update trigger system - check for enter/exit events
   * @param positions Map of entity ID to position (player + NPCs)
   * @param deltaTime Frame delta time
   */
  
  update(positions: Map<string, Vector3>, deltaTime: number): void {
    // Debug: Log first call to verify update is being called
    if (!this._firstUpdateLogged) {
      this._firstUpdateLogged = true;
      const triggerCount = this.triggers.size;
      const triggerIds = Array.from(this.triggers.keys()).join(', ');
      const positionEntries = Array.from(positions.entries()).map(([id, pos]) => `${id}:(${pos.x.toFixed(2)},${pos.y.toFixed(2)},${pos.z.toFixed(2)})`).join(', ');
      logger.behavior.debug(`🔶 [TRIGGERS] First call! Triggers: ${triggerCount} [${triggerIds}], Entities: ${positions.size} [${positionEntries}], ServiceRegistry.isInitialized: ${ServiceRegistry.isInitialized()}`);
    }
    
    if (!ServiceRegistry.isInitialized()) return;
    
    const services = ServiceRegistry.getServices();
    
    for (const [_triggerId, trigger] of this.triggers) {
      // Skip one-shot triggers that already activated
      if (trigger.oneShot && trigger.activated) continue;
      
      const previouslyInside = new Set(trigger.entitiesInside);
      const currentlyInside = new Set<string>();
      
      // Check each monitored entity
      for (const [entityId, position] of positions) {
        const isInside = this.isInsideTrigger(trigger, position);
        
        if (isInside) {
          currentlyInside.add(entityId);
          
          // Check for enter event
          if (!previouslyInside.has(entityId)) {
            trigger.entitiesInside.add(entityId);
            this.fireEnterEvent(trigger, entityId, services);
            
            if (trigger.oneShot) {
              trigger.activated = true;
            }
          }
        }
      }
      
      // Check for exit events
      for (const entityId of previouslyInside) {
        if (!currentlyInside.has(entityId)) {
          trigger.entitiesInside.delete(entityId);
          this.fireExitEvent(trigger, entityId);
        }
      }
      
      // Fire tick events for entities still inside
      if (trigger.entitiesInside.size > 0) {
        this.fireTickEvent(trigger, deltaTime, services);
      }
    }
  }
  
  /**
   * Convenience method for single entity (e.g., just player)
   */
  updateSingle(entityId: string, position: Vector3, deltaTime: number): void {
    const positions = new Map<string, Vector3>();
    positions.set(entityId, position);
    this.update(positions, deltaTime);
  }
  
  // ============================================
  // EVENT FIRING
  // ============================================
  
  /**
   * Fire enter event for a trigger
   */
  private fireEnterEvent(trigger: TriggerState, entityId: string, services: SceneServices): void {
    const { behavior } = BehaviorRegistry.get(trigger.behaviorId);
    const triggerBehavior = behavior as TriggerBehavior;
    
    if (!triggerBehavior.onEnter) return;
    
    const entrant = this.entityRegistry.getEntityInfo(entityId);
    if (!entrant) return;
    
    const entityInfo = this.entityRegistry.getEntityInfo(trigger.id);
    if (!entityInfo) return;
    
    // Attach metadata to entity info for behavior state storage
    entityInfo.metadata = entityInfo.metadata || {};
    Object.assign(entityInfo.metadata, trigger.metadata);
    
    const ctx: TriggerContextBase & { entrant: EntityInfo } = {
      self: entityInfo,
      position: trigger.position,
      params: trigger.params,
      oneShot: trigger.oneShot,
      entrant
    };
    
    const api: TriggerEnterAPI = { ctx, services };
    
    try {
      logger.triggers.debug(`Trigger ${trigger.id}: entity ${entityId} entered`);
      triggerBehavior.onEnter(api);
      
      // Sync metadata back
      trigger.metadata = entityInfo.metadata as Record<string, unknown>;
    } catch (error) {
      logger.triggers.error(`Error in trigger ${trigger.id} onEnter:`, error);
    }
    
    // Emit event for other systems
    this.eventBus.emit('trigger:enter', {
      triggerId: trigger.id,
      behaviorId: trigger.behaviorId,
      entityId,
      position: trigger.position
    });
  }
  
  /**
   * Fire exit event for a trigger
   */
  private fireExitEvent(trigger: TriggerState, entityId: string): void {
    if (!ServiceRegistry.isInitialized()) return;
    
    const { behavior } = BehaviorRegistry.get(trigger.behaviorId);
    const triggerBehavior = behavior as TriggerBehavior;
    
    if (!triggerBehavior.onExit) return;
    
    const exiter = this.entityRegistry.getEntityInfo(entityId);
    if (!exiter) return;
    
    const entityInfo = this.entityRegistry.getEntityInfo(trigger.id);
    if (!entityInfo) return;
    
    // Restore metadata
    entityInfo.metadata = entityInfo.metadata || {};
    Object.assign(entityInfo.metadata, trigger.metadata);
    
    const services = ServiceRegistry.getServices();
    
    const ctx: TriggerContextBase & { exiter: EntityInfo } = {
      self: entityInfo,
      position: trigger.position,
      params: trigger.params,
      oneShot: trigger.oneShot,
      exiter
    };
    
    const api: TriggerExitAPI = { ctx, services };
    
    try {
      logger.triggers.debug(`Trigger ${trigger.id}: entity ${entityId} exited`);
      triggerBehavior.onExit(api);
      
      // Sync metadata back
      trigger.metadata = entityInfo.metadata as Record<string, unknown>;
    } catch (error) {
      logger.triggers.error(`Error in trigger ${trigger.id} onExit:`, error);
    }
    
    // Emit event for other systems
    this.eventBus.emit('trigger:exit', {
      triggerId: trigger.id,
      behaviorId: trigger.behaviorId,
      entityId,
      position: trigger.position
    });
  }
  
  /**
   * Fire tick event for entities inside trigger
   */
  private fireTickEvent(trigger: TriggerState, deltaTime: number, services: SceneServices): void {
    const { behavior } = BehaviorRegistry.get(trigger.behaviorId);
    const triggerBehavior = behavior as TriggerBehavior;
    
    if (!triggerBehavior.onTick) return;
    
    const entityInfo = this.entityRegistry.getEntityInfo(trigger.id);
    if (!entityInfo) return;
    
    // Restore metadata
    entityInfo.metadata = entityInfo.metadata || {};
    Object.assign(entityInfo.metadata, trigger.metadata);
    
    const ctx: TriggerContextBase & { entitiesInside: ReadonlySet<string>; deltaTime: number } = {
      self: entityInfo,
      position: trigger.position,
      params: trigger.params,
      oneShot: trigger.oneShot,
      entitiesInside: trigger.entitiesInside,
      deltaTime
    };
    
    const api: TriggerTickAPI = { ctx, services };
    
    try {
      triggerBehavior.onTick(api);
      
      // Sync metadata back
      trigger.metadata = entityInfo.metadata as Record<string, unknown>;
    } catch (error) {
      logger.triggers.error(`Error in trigger ${trigger.id} onTick:`, error);
    }
  }
  
  // ============================================
  // QUERIES
  // ============================================
  
  /**
   * Get all triggers
   */
  getTriggers(): TriggerState[] {
    return Array.from(this.triggers.values());
  }
  
  /**
   * Get trigger by ID
   */
  getTrigger(id: string): TriggerState | undefined {
    return this.triggers.get(id);
  }
  
  /**
   * Get triggers by behavior ID
   */
  getTriggersByBehavior(behaviorId: string): TriggerState[] {
    return Array.from(this.triggers.values()).filter(t => t.behaviorId === behaviorId);
  }
  
  /**
   * Reset a trigger (for one-shot triggers)
   */
  resetTrigger(id: string): void {
    const trigger = this.triggers.get(id);
    if (trigger) {
      trigger.activated = false;
    }
  }
  
  /**
   * Reset all triggers
   */
  resetAll(): void {
    for (const trigger of this.triggers.values()) {
      trigger.activated = false;
    }
  }

  // ============================================
  // DEBUG VISUALIZATION
  // ============================================

  /**
   * Enable or disable debug visualization of trigger zones
   * Creates transparent meshes showing trigger bounds
   */
  setDebugVisualization(enabled: boolean): void {
    if (enabled === this.debugVisualizationEnabled) return;
    
    this.debugVisualizationEnabled = enabled;
    
    if (enabled) {
      this.createDebugMeshes();
      this.createPlayerDebugSphere();
    } else {
      this.clearDebugMeshes();
    }
    
    logger.triggers.log(`🔍 Trigger debug visualization: ${enabled ? 'ON' : 'OFF'}`);
  }

  /**
   * Check if debug visualization is enabled
   */
  isDebugVisualizationEnabled(): boolean {
    return this.debugVisualizationEnabled;
  }

  /**
   * Create debug meshes for all registered triggers
   */
  private createDebugMeshes(): void {
    if (!this.scene) {
      logger.triggers.warn('Cannot create debug meshes: scene not set. Call setScene() first.');
      return;
    }

    for (const [id, trigger] of this.triggers) {
      this.createDebugMeshForTrigger(id, trigger);
    }
  }

  /**
   * Create a debug mesh for a single trigger
   */
  private createDebugMeshForTrigger(id: string, trigger: TriggerState): void {
    if (!this.scene) return;
    if (this.debugMeshes.has(id)) return; // Already exists

    let debugMesh: AbstractMesh | null = null;
    
    // If trigger has a mesh reference, clone it as wireframe
    if (trigger.mesh) {
      const mesh = trigger.mesh as Mesh;
      mesh.computeWorldMatrix(true);
      
      // Get world-space AABB from vertices
      const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
      if (positions && positions.length >= 3) {
        const worldMatrix = mesh.getWorldMatrix();
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        
        for (let i = 0; i < positions.length; i += 3) {
          const localVert = new Vector3(positions[i], positions[i + 1], positions[i + 2]);
          const worldVert = Vector3.TransformCoordinates(localVert, worldMatrix);
          
          if (worldVert.x < minX) minX = worldVert.x;
          if (worldVert.y < minY) minY = worldVert.y;
          if (worldVert.z < minZ) minZ = worldVert.z;
          if (worldVert.x > maxX) maxX = worldVert.x;
          if (worldVert.y > maxY) maxY = worldVert.y;
          if (worldVert.z > maxZ) maxZ = worldVert.z;
        }
        
        // Create box from AABB
        const width = maxX - minX;
        const height = maxY - minY;
        const depth = maxZ - minZ;
        const center = new Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
        
        debugMesh = MeshBuilder.CreateBox(`trigger_debug_${id}`, {
          width, height, depth
        }, this.scene);
        debugMesh.position.copyFrom(center);
        
        logger.triggers.log(`📦 Debug box for ${id}: center=(${center.x.toFixed(2)}, ${center.y.toFixed(2)}, ${center.z.toFixed(2)}) size=(${width.toFixed(2)}, ${height.toFixed(2)}, ${depth.toFixed(2)})`);
      }
    } else if (trigger.boundingBox) {
      // Use static bounding box
      const bb = trigger.boundingBox;
      const size = bb.maximum.subtract(bb.minimum);
      const center = bb.minimum.add(size.scale(0.5));
      
      debugMesh = MeshBuilder.CreateBox(`trigger_debug_${id}`, {
        width: size.x,
        height: size.y,
        depth: size.z
      }, this.scene);
      debugMesh.position.copyFrom(center);
    } else {
      // No valid geometry - cannot create debug mesh
      logger.triggers.warn(`⚠️ Trigger ${id} has no mesh or bounding box, skipping debug visualization`);
      return;
    }

    if (!debugMesh) {
      logger.triggers.warn(`⚠️ Trigger ${id} failed to create debug mesh`);
      return;
    }

    // Apply transparent material
    const material = new StandardMaterial(`trigger_debug_mat_${id}`, this.scene);
    material.diffuseColor = new Color3(0, 1, 0.5); // Cyan-green
    material.emissiveColor = new Color3(0, 0.3, 0.15);
    material.alpha = 0.3;
    material.wireframe = false;
    material.backFaceCulling = false;
    debugMesh.material = material;

    debugMesh.isPickable = false;
    debugMesh.checkCollisions = false;
    debugMesh.receiveShadows = false;
    debugMesh.metadata = { ...(debugMesh.metadata || {}), isDebug: true, noShadows: true };
    // Visible on all layers - no layerMask restriction

    this.debugMeshes.set(id, debugMesh);
    logger.triggers.log(`✅ Debug mesh created and visible: ${debugMesh.name}`);
  }

  /**
   * Clear all debug meshes
   */
  private clearDebugMeshes(): void {
    for (const [, mesh] of this.debugMeshes) {
      mesh.dispose();
    }
    this.debugMeshes.clear();
    
    if (this.playerDebugSphere) {
      this.playerDebugSphere.dispose();
      this.playerDebugSphere = null;
    }
  }

  /**
   * Create player position debug sphere
   */
  private createPlayerDebugSphere(): void {
    if (!this.scene || this.playerDebugSphere) return;

    this.playerDebugSphere = MeshBuilder.CreateSphere('debug_player_position', {
      diameter: 0.5,
      segments: 8
    }, this.scene);

    const material = new StandardMaterial('debug_player_mat', this.scene);
    material.diffuseColor = new Color3(1, 0, 0); // Red
    material.emissiveColor = new Color3(0.5, 0, 0);
    material.alpha = 0.8;
    this.playerDebugSphere.material = material;
    this.playerDebugSphere.isPickable = false;
    this.playerDebugSphere.checkCollisions = false;
    this.playerDebugSphere.receiveShadows = false;
    this.playerDebugSphere.layerMask = 0x20000000; // Debug layer - filterMesh excludes from shadows
    this.playerDebugSphere.metadata = { isDebug: true, noShadows: true };

    logger.triggers.log('🔴 Player debug sphere created');
  }

  /**
   * Update player debug sphere position
   * Call this from update() or externally each frame
   */
  updatePlayerDebugPosition(position: Vector3): void {
    if (!this.debugVisualizationEnabled) return;

    if (!this.playerDebugSphere && this.scene) {
      this.createPlayerDebugSphere();
    }

    if (this.playerDebugSphere) {
      this.playerDebugSphere.position.copyFrom(position);
    }
  }

  /**
   * Update debug mesh position (for dynamically moving triggers)
   */
  updateDebugMesh(id: string): void {
    const trigger = this.triggers.get(id);
    const debugMesh = this.debugMeshes.get(id);
    
    if (!trigger || !debugMesh || !trigger.mesh) return;

    // Re-create the debug mesh if trigger has moved
    debugMesh.dispose();
    this.debugMeshes.delete(id);
    this.createDebugMeshForTrigger(id, trigger);
  }
  
  // ============================================
  // CLEANUP
  // ============================================
  
  /**
   * Clear all triggers
   */
  clear(): void {
    for (const id of Array.from(this.triggers.keys())) {
      this.unregister(id);
    }
    this.clearDebugMeshes();
    logger.triggers.debug('TriggerSystem cleared');
  }
  
  /**
   * Dispose the system
   */
  dispose(): void {
    this.clear();
    this.scene = null;
  }
}
